import React, { useState, useRef } from 'react';
import { ArrowLeft, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShotContextChipPicker } from './ShotContextChipPicker';
import type { ShotIssueTag } from '@/types/shotIssues';
import { deriveShotIssueCategory } from '@/types/shotIssues';

interface SwingUploadShotContextProps {
  initialTags?: ShotIssueTag[];
  initialNotes?: string | null;
  onContinue: (tags: ShotIssueTag[], category: string | null, notes: string | null) => void;
  onBack: () => void;
  onSkip: () => void;
  selectedFile?: File | null;
  onFileSelect?: (file: File) => void;
  className?: string;
  isUploading?: boolean;
}

export function SwingUploadShotContext({
  initialTags = [],
  initialNotes = null,
  onContinue,
  onBack,
  onSkip,
  selectedFile,
  onFileSelect,
  className,
  isUploading = false,
}: SwingUploadShotContextProps) {
  const [selectedTags, setSelectedTags] = useState<ShotIssueTag[]>(initialTags);
  const [notes, setNotes] = useState<string>(initialNotes || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContinue = () => {
    if (!selectedFile) {
      alert('Please select a video file first');
      return;
    }
    const category = deriveShotIssueCategory(selectedTags);
    onContinue(selectedTags, category, notes.trim() || null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onFileSelect) {
      if (file.size > 300 * 1024 * 1024) { // 300MB limit
        alert("File size must be under 300MB");
        return;
      }
      if (!file.type.startsWith("video/")) {
        alert("Please select a video file");
        return;
      }
      onFileSelect(file);
    }
  };

  return (
    <div className={className}>
      {/* File Selection */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-[#0D382A] mb-3">
          1. Select Your Video
        </h2>
        {!selectedFile ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 mb-3">Choose a video file to upload</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:opacity-90"
            >
              Select Video File
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.mov,.webm,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800">✓ Video Selected</p>
                <p className="text-sm text-green-600">{selectedFile.name}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="text-green-700 border-green-300"
              >
                Change File
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.mov,.webm,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl text-[#0D382A] mb-3">
          2. What happened on this shot?
        </h1>
        <p className="text-gray-600">
          Optional, but helps us match your swing faults to what you're seeing on the course.
        </p>
      </div>

      {/* Chip Picker */}
      <ShotContextChipPicker
        selectedTags={selectedTags}
        notes={notes}
        onTagsChange={setSelectedTags}
        onNotesChange={setNotes}
        className="mb-8"
      />

      {/* Action Bar */}
      <div className="flex items-center justify-between pt-8">
        <Button
          variant="ghost"
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-700"
        >
          Skip for now
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedFile}
          className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] px-8 py-3 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selectedFile ? 'Upload & Analyze' : 'Select Video First'}
        </Button>
      </div>
    </div>
  );
}

